/**
 * useDemoLimits hook
 * Derives freemium limit state from the user's profile (via usePebbles).
 * Premium users and MCP sessions bypass all limits.
 *
 * Used by: SessionsListPage (US-003/US-004), SessionChatPage (US-005/US-006)
 *
 * Limit values are sourced from the backend tierLimits field in the profile response.
 * DEMO_LIMITS constants are used as fallback defaults when tierLimits is absent
 * (backward compatibility during phased rollout).
 */

import { usePebbles } from '@/hooks/usePebbles';
import { DEMO_LIMITS } from '@/constants/demoLimits';

export interface DemoLimits {
  /** True if the user is a non-premium (limited) user (covers free, demo, and any future non-premium tiers) */
  isFreeUser: boolean;

  /** Maximum lifetime sessions (number for limited users, null for unlimited/premium) */
  maxSessions: number | null;

  /** Maximum questions per session (number for limited users, null for unlimited/premium) */
  maxQuestionsPerSession: number | null;

  /** Maximum attempts per question (number for limited users, null for unlimited/premium) */
  maxAttemptsPerQuestion: number | null;

  /** The user's tier string. Widened to string to handle unknown future tiers gracefully. */
  userTier: string;

  /** True while the profile is still loading */
  isLoading: boolean;
}

export function useDemoLimits(): DemoLimits {
  const { profile, isLoading } = usePebbles();

  // Default to premium (no limits) while loading or if profile is unavailable.
  // This prevents flash-of-limited-state on page load and matches the backend
  // default in get_user_profile.py: user.get('userTier', 'premium')
  const userTier = profile?.userTier ?? 'premium';

  // Any non-premium user is a limited user (covers free, demo, and unknown future tiers)
  const isLimitedUser = userTier !== 'premium';

  // isFreeUser is kept as the exported name for backward compatibility with consumers
  // (SessionsListPage, SessionChatPage). Its meaning is now "is limited user".
  const isFreeUser = isLimitedUser;

  return {
    isFreeUser,
    userTier,
    // US-004 edge case: if maxSessions is explicitly null from backend, treat as unlimited
    // (undefined/missing field falls back to tierLimits or DEFAULT_MAX_SESSIONS; explicit null means no cap)
    maxSessions: isLimitedUser
      ? profile?.maxSessions === undefined
        ? (profile?.tierLimits?.maxLifetimeSessions ?? DEMO_LIMITS.DEFAULT_MAX_SESSIONS)
        : profile?.maxSessions
      : null,
    // Read from API tierLimits field when available; fall back to DEMO_LIMITS constants
    // for backward compatibility when backend has not yet returned tierLimits.
    maxQuestionsPerSession: isLimitedUser
      ? (profile?.tierLimits?.maxQuestionsPerSession ?? DEMO_LIMITS.MAX_QUESTIONS_PER_SESSION)
      : null,
    maxAttemptsPerQuestion: isLimitedUser
      ? (profile?.tierLimits?.maxAttemptsPerQuestion ?? DEMO_LIMITS.MAX_ATTEMPTS_PER_QUESTION)
      : null,
    isLoading,
  };
}
